/**
 * The zod-free half of `shopify-gid.ts`.
 *
 * This module is bundled into Shopify UI extensions through
 * `@intelligems/ig-utils`, which has a 64 KB compressed ceiling. It must stay
 * free of runtime imports — no zod, no barrel, nothing. See
 * `extensions/checkout-ui/__tests__/bundle-size.spec.ts`.
 */
import type { ShopifyGqlIdType } from "../dtos/shopify-product-variants";

/**
 * Shopify's numeric node ids are always digits, so this is the whole set of
 * bare ids `toShopifyGqlIdIfBare` can encounter.
 */
const BARE_SHOPIFY_ID = /^\d+$/;

/**
 * Prefixes an id into GID form. Blind: it does not check that `id` is
 * actually an id. Use it when the input is KNOWN to be one.
 */
export function toShopifyGqlId(id: string, type: ShopifyGqlIdType): string {
  if (id.includes("gid")) {
    return id;
  }
  return `gid://shopify/${type}/${id}`;
}

/**
 * Promotes a bare numeric id to GID form, and leaves everything else alone.
 *
 * The guard is the point, and it is why this is not `toShopifyGqlId`.
 * `toShopifyGqlId` prefixes blindly, so
 * `toShopifyGqlId("not-a-thing", "Product")` returns
 * `gid://shopify/Product/not-a-thing` — a GID that LOOKS canonical and
 * resolves to nothing, trading a visible bad id for a hidden one. Use
 * `toShopifyGqlId` when the input is known to be an id; use this at a boundary
 * where the input is whatever someone typed or pasted.
 *
 * ONLY all-digit ids are promoted. Anything else is either already a GID
 * (returned untouched, which is what makes this idempotent) or an
 * identifier this has no business rewriting — it passes through so a bad id
 * stays visibly bad and the caller's own validation can reject it.
 */
export function toShopifyGqlIdIfBare(
  id: string,
  type: ShopifyGqlIdType
): string {
  return BARE_SHOPIFY_ID.test(id) ? toShopifyGqlId(id, type) : id;
}
